import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Webcam from "react-webcam";
import PropsTypes from "prop-types";

import { Modal, ModalFooter, ModalText } from "shared/components/Modals/Modal";
import SpinLoader, {
  LOADER_COLOR_DARK,
  LOADER_SIZE_LARGE,
  POSITION_ABSOLUTE,
} from "shared/components/SpinLoader";

import { UPLOAD_IMAGE_FILE_FORMATS } from "shared/constants/files";

import { uploadFileValidator } from "shared/helpers/media";

import stylesModal from "@platformAssets/components/Modal.module.scss";
import styles from "@platformAssets/components/WebCameraModal.module.scss";

const WebCameraModal = ({
  close,
  addFileItems,
  handleError,
  showFileError,
}) => {
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation("profile");
  const webCamRef = useRef(null);

  const videoConstraints = {
    width: 1024,
    height: 720,
    facingMode: "user",
  };

  /**
   * Take snapshot from web camera
   * @type {(function(): void)|*}
   */
  const handleCapture = useCallback(() => {
    if (!webCamRef?.current || loading) {
      return;
    }

    const imageSrc = webCamRef?.current.getScreenshot();
    const file = getFileFromSnapshot(imageSrc, "snapshot.jpg");

    const { status, message, fileType } = uploadFileValidator(
      file,
      UPLOAD_IMAGE_FILE_FORMATS
    );
    const temporaryUid = `file_webcam_${new Date().getTime().toString()}`;
    const tmpFile = {
      uid: temporaryUid,
      file,
      type: fileType,
      uploaded: false,
      send: false,
      error: {
        status: !status,
        message,
      },
    };

    if (!status) {
      showFileError({ show: true, text: message });
    }

    addFileItems({ files: [tmpFile] });
  }, [webCamRef, loading]);

  /**
   * Create file object from web camera snapshot
   * @param {string} src Web camera snapshot source
   * @param {string} filename Snaphot name
   * @return {File}
   */
  const getFileFromSnapshot = (src, filename) => {
    const arr = src.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  return (
    <Modal close={close} bodyClass={stylesModal.xlargeModal} withPortal>
      <ModalText text={t("profile.webcam.title")} isTitle={true} />
      <ModalText text={t("profile.webcam.desc")} />

      <div className={styles.WebCameraModal__wrap}>
        <Webcam
          audio={false}
          screenshotFormat="image/jpeg"
          minScreenshotHeight={720}
          minScreenshotWidth={1024}
          videoConstraints={videoConstraints}
          screenshotQuality={0.98}
          mirrored
          onUserMedia={() => {
            setTimeout(() => setLoading(false), 1000);
          }}
          onUserMediaError={() => handleError()}
          ref={webCamRef}
        />
        {loading && (
          <SpinLoader
            size={LOADER_SIZE_LARGE}
            color={LOADER_COLOR_DARK}
            position={POSITION_ABSOLUTE}
            withBlur={true}
          />
        )}
      </div>

      <ModalFooter customClass={styles.WebCameraModal__footer}>
        <button
          className={`${stylesModal.primaryButton} ${
            loading ? stylesModal.disabled : ""
          }`}
          onClick={handleCapture}
        >
          {t("profile.webcam.btn")}
        </button>
      </ModalFooter>
    </Modal>
  );
};

WebCameraModal.propTypes = {
  close: PropsTypes.func.isRequired,
  handleError: PropsTypes.func.isRequired,
  showFileError: PropsTypes.func.isRequired,
  addFileItems: PropsTypes.func.isRequired,
};

export default WebCameraModal;
